/**
 * #979 — card source domain types.
 *
 * The shapes follow the proposal in `docs/260926FeatureDeliveryContracts.md`
 * §1 ("Senior persistence baseline" and "Patricia: complete source selection
 * interaction"). They are a CLIENT contract for the adapter seam, not an
 * endpoint promise: #976 owns identity, schema, authorization and the live
 * adapter, and may reshape the wire format behind `CardSourceAdapter` without
 * touching the selector.
 *
 * The target IS #978's `CardNoteTarget` (`src/lib/cardNotes/types.ts`): a card
 * names one thing for both annotations, so Source reuses the notes identity
 * rather than declaring a second one that could drift.
 */
import {
  targetKey as noteTargetKey,
  withScope as noteWithScope,
  type CardNoteSubject,
  type CardNoteTarget,
  type CardNoteVenue,
} from '../cardNotes/types';

export type CardSourceVenue = CardNoteVenue;
/** Exactly one source-bearing thing — see `CardNoteTarget`. */
export type CardSourceTarget = CardNoteTarget;
/** A target without its scope — what a card knows about itself. */
export type CardSourceSubject = CardNoteSubject;

/**
 * One name in the signed-in trader's private dictionary (contract: "private
 * to that user/org"). `id` is stable and is what an assignment stores; the
 * name is display only, so a card never re-maps to a different source that
 * happens to share a label.
 */
export interface CardSource {
  id: string;
  name: string;
}

/**
 * The canonical assignment for one target. `sourceId: null` with
 * `revision: 0` = never assigned; `sourceId: null` with a revision = cleared.
 */
export interface CardSourceAssignment {
  target: CardSourceTarget;
  sourceId: string | null;
  /** Server revision. A write names the revision it was based on. */
  revision: number;
  /** ISO 8601 of the last write, or null when never written. */
  updatedAt: string | null;
}

/** What a create returns: the stored source, and whether it already existed. */
export interface CardSourceCreated {
  source: CardSource;
  /**
   * True when the name matched an existing entry (case-insensitively) and
   * that entry was returned instead of a second one (contract: "Reuse a
   * duplicate source rather than silently creating another").
   */
  reused: boolean;
}

/**
 * The persistence seam. Dictionary reads/creates and target assignment are
 * separate operations (contract §1). The dictionary belongs to the signed-in
 * user/org, which the SERVER resolves from the session — the client never
 * sends it. The production implementation is `createCardSourceAdapter` over
 * the card-annotation client (`lib/cardAnnotations/adapters.ts`); there is
 * deliberately no localStorage fallback.
 */
export interface CardSourceAdapter {
  /** The trader's whole dictionary, in any order (the UI sorts it). */
  listSources(): Promise<readonly CardSource[]>;
  /**
   * Add `name` to the dictionary. Never assigns it to anything (contract:
   * "Creating a name adds it to the list without selecting it"). Rejects with
   * a `CardSourceWriteError('invalid_name')` for a name the rules refuse.
   * A live implementation must time out rather than hang: the add dialog
   * can be closed while waiting, but cannot add again until this settles.
   */
  createSource(name: string): Promise<CardSourceCreated>;
  /**
   * Canonical assignments for a bounded batch of targets. A target missing
   * from the result has never been assigned (treated as unassigned).
   *
   * Each row must echo its target EXACTLY as requested (same `scope` string,
   * same identity fields): rows are matched by `targetKey`, so a server that
   * normalised the opaque scope would leave every card looking unassigned.
   */
  readAssignments(targets: readonly CardSourceTarget[]): Promise<readonly CardSourceAssignment[]>;
  /**
   * Set (`sourceId`) or clear (`null`) this target's source against
   * `expectedRevision`. Clear removes only this assignment; the name stays in
   * the dictionary. Resolves with the canonical saved assignment (its target
   * echoed exactly — a mismatch is treated as a failed write); rejects with
   * a `CardSourceWriteError` on conflict or refusal, or with anything else on
   * transport failure.
   */
  assign(
    target: CardSourceTarget,
    sourceId: string | null,
    expectedRevision: number,
  ): Promise<CardSourceAssignment>;
}

export type CardSourceWriteReason = 'conflict' | 'forbidden' | 'unknown_source' | 'invalid_name';

const WRITE_MESSAGES: Record<CardSourceWriteReason, string> = {
  conflict: 'Source changed since it was loaded',
  forbidden: 'Not allowed to change this source',
  unknown_source: 'That source no longer exists',
  invalid_name: 'That source name is not allowed',
};

/**
 * Typed write refusal. `conflict` carries the canonical assignment that won,
 * so the selector can show it instead of overwriting silently.
 */
export class CardSourceWriteError extends Error {
  readonly reason: CardSourceWriteReason;
  readonly latest: CardSourceAssignment | null;

  constructor(reason: 'conflict', latest: CardSourceAssignment);
  constructor(reason: Exclude<CardSourceWriteReason, 'conflict'>, latest?: null);
  constructor(reason: CardSourceWriteReason, latest: CardSourceAssignment | null = null) {
    super(WRITE_MESSAGES[reason]);
    this.name = 'CardSourceWriteError';
    this.reason = reason;
    this.latest = latest;
  }
}

/** Stable string identity for a target: the store key (shared with notes). */
export const targetKey: (t: CardSourceTarget) => string = noteTargetKey;

export const withScope: (subject: CardSourceSubject, scope: string) => CardSourceTarget = noteWithScope;
